import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema';

export type { components, paths };
export type ProblemDetails = components['schemas']['ProblemDetails'];
export type Schemas = components['schemas'];

export type ApiClient = ReturnType<typeof createApiClient>;

export interface ApiClientOptions {
  /** Defaults to the same-origin API path. */
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
}

/** Thrown for non-2xx responses; carries the Problem Details body (API-003). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails | undefined,
  ) {
    super(problem?.code ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.problem?.code ?? 'unknown';
  }

  get params(): Record<string, string> {
    return this.problem?.params ?? {};
  }
}

/**
 * Middleware for mutating requests: adds an `Idempotency-Key` (API-006) and
 * the CSRF header required for cookie authenticated requests.
 */
export const idempotencyMiddleware: Middleware = {
  onRequest({ request }) {
    if (request.method !== 'GET' && request.method !== 'HEAD') {
      if (!request.headers.has('Idempotency-Key')) {
        request.headers.set('Idempotency-Key', crypto.randomUUID());
      }
      request.headers.set('X-Swarm-Csrf', '1');
    }
    return request;
  },
};

export function createApiClient({ baseUrl = '/api/v1', fetch }: ApiClientOptions = {}) {
  const client = createClient<paths>({
    baseUrl: resolveBaseUrl(baseUrl),
    credentials: 'include',
    // Resolve `fetch` per call (not at creation) so tests and polyfills can replace it.
    fetch: fetch ?? ((input: Request) => globalThis.fetch(input)),
  });
  client.use(idempotencyMiddleware);
  return client;
}

/** Unwraps an openapi-fetch result or throws an `ApiError`. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error as ProblemDetails | undefined);
  }
  return result.data as T;
}

/** Like `unwrap` for responses without body (202, 204). */
export function expectOk(result: { error?: unknown; response: Response }): void {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error as ProblemDetails | undefined);
  }
}

/** Makes a relative base URL absolute (fetch's `Request` needs an absolute URL outside browsers). */
function resolveBaseUrl(baseUrl: string): string {
  const origin = globalThis.location?.origin ?? 'http://localhost';
  return new URL(baseUrl, origin).toString().replace(/\/$/, '');
}
